import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Globe, ShoppingCart, Truck, Play,
  Boxes, Receipt, TrendingUp, Package, Users, DollarSign,
  Ship, MapPin, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useTheme } from "@/context/ThemeContext";

/**
 * Demos gallery — three rich demo cards with preview thumbnails + key stats
 * from the underlying demo accounts. Clicking "Watch Demo" opens a modal
 * with the narrated MP4 (clear close button — never traps the user in a
 * fullscreen video). Clicking "Try It Live" goes to the interactive
 * walkthrough at /demo/play/:category.
 */
const DEMOS = [
  {
    id: "grocery",
    name: "Maple Grove Market",
    tagline: "Grocery / Convenience Store",
    icon: ShoppingCart,
    accent: "emerald",
    description:
      "A neighborhood grocery using My Business Portal to track every sale, every receipt, and every product across registers. Connected to Clover.",
    location: "Madison, WI · 318 Maple Grove Ave",
    stats: [
      { icon: Package, label: "60 products", sub: "9 categories" },
      { icon: Receipt, label: "150+ receipts", sub: "rolling 3 weeks" },
      { icon: DollarSign, label: "Profit + tax tracking", sub: "live" },
      { icon: TrendingUp, label: "Expiry alerts", sub: "+ reorder thresholds" },
    ],
    features: [
      "Live POS via Clover", "EBT / Cash / Card split", "Expiry alerts",
      "Reorder thresholds", "Tax filing exports",
    ],
    video: "/demos/grocery.mp4",
    preview: "/demos/grocery-preview.jpg?v=3",
    interactive: "/demo/play/grocery",
  },
  {
    id: "wholesale",
    name: "Atlas Trade Imports, LLC",
    tagline: "Import / Export Wholesale",
    icon: Globe,
    accent: "cyan",
    description:
      "A wholesale importer of specialty goods from Turkey, China, and Italy. Tracks every container from supplier to warehouse with landed cost and customs clearance.",
    location: "Long Beach, CA · 4477 Harbor Blvd",
    stats: [
      { icon: Ship, label: "7 shipments", sub: "in transit + received" },
      { icon: Package, label: "114 products", sub: "Turkey · China · Italy" },
      { icon: DollarSign, label: "Landed cost", sub: "auto-calculated" },
      { icon: MapPin, label: "3 origin ports", sub: "Mersin · Izmir · Shanghai" },
    ],
    features: [
      "Container tracking", "Landed cost calc", "AI bulk uploads",
      "Customer book", "Invoicing + Zelle/ACH",
    ],
    video: "/demos/wholesale.mp4",
    preview: "/demos/wholesale-preview.jpg?v=3",
    interactive: "/demo/play/wholesale",
  },
  {
    id: "mechanic",
    name: "Riverside Auto Service, LLC",
    tagline: "Mechanic Shop / Auto Repair",
    icon: Truck,
    accent: "amber",
    description:
      "A repair shop using My Business Portal for invoicing, parts inventory, customer vehicle history, and tax tracking. Built for shop life, not for accountants.",
    location: "Springfield, IL · 1820 Riverside Pkwy",
    stats: [
      { icon: Receipt, label: "57 invoices", sub: "paid + pending" },
      { icon: Boxes, label: "Parts inventory", sub: "cost + sell + markup" },
      { icon: Users, label: "Active customers", sub: "with full job history" },
      { icon: DollarSign, label: "Parts / Labor split", sub: "real margin clarity" },
    ],
    features: [
      "60-second invoicing", "Parts/Labor split", "Tax tracking",
      "AI bulk parts upload", "Customer vehicle book",
    ],
    video: "/demos/mechanic.mp4",
    preview: "/demos/mechanic-preview.jpg?v=3",
    interactive: "/demo/play/mechanic",
  },
];

const ACCENT = {
  emerald: { ring: "ring-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-400", btn: "from-emerald-500 to-emerald-600" },
  cyan:    { ring: "ring-cyan-500/40",    bg: "bg-cyan-500/10",    text: "text-cyan-400",    btn: "from-cyan-500 to-blue-600" },
  amber:   { ring: "ring-amber-500/40",   bg: "bg-amber-500/10",   text: "text-amber-400",   btn: "from-amber-500 to-orange-600" },
};

export default function Demos() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(null); // demo currently playing in modal

  // Auto-open the modal when arriving via /demos?play=<id> (from the
  // Landing page category cards). Clears the param when the modal closes.
  useEffect(() => {
    const id = searchParams.get("play");
    if (id && !active) {
      const demo = DEMOS.find((d) => d.id === id);
      if (demo) setActive(demo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleModalChange = (open) => {
    if (!open) {
      setActive(null);
      if (searchParams.has("play")) {
        searchParams.delete("play");
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-900"}`}>
      {/* Sticky top bar */}
      <div className={`sticky top-0 z-40 border-b backdrop-blur ${isDark ? "bg-slate-950/85 border-slate-800" : "bg-white/85 border-slate-200"}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="demos-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </Link>
          <Link to="/signup">
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="demos-get-started-btn">
              Get Started
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-8 text-center">
        <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-cyan-400 mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Live demo accounts
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          Three real businesses. Three real workflows.
        </h1>
        <p className={`mt-3 text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Each demo is a working account with real data — products, customers, sales, shipments — so you can see exactly what your business will look like.
        </p>
      </div>

      {/* Demo cards */}
      <div className="max-w-6xl mx-auto px-4 pb-12 space-y-6">
        {DEMOS.map((demo) => {
          const Icon = demo.icon;
          const a = ACCENT[demo.accent];
          return (
            <article
              key={demo.id}
              className={`rounded-2xl overflow-hidden border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} shadow-sm`}
              data-testid={`demo-card-${demo.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-0">
                {/* Preview thumbnail with play overlay */}
                <button
                  onClick={() => setActive(demo)}
                  className="relative group block bg-slate-900 aspect-video lg:aspect-auto"
                  data-testid={`demo-card-${demo.id}-play-thumb`}
                >
                  <img
                    src={demo.preview}
                    alt={`${demo.name} dashboard preview`}
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/95 text-slate-900 shadow-2xl scale-95 group-hover:scale-100 transition-transform`}>
                      <Play className="w-5 h-5 fill-current" />
                      <span className="font-semibold text-sm">Watch demo</span>
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                    <span className="text-[11px] uppercase tracking-wider font-semibold bg-white/15 backdrop-blur px-2 py-1 rounded">
                      {demo.tagline}
                    </span>
                    <span className="text-[11px] opacity-80">Narrated · ~1–2 min</span>
                  </div>
                </button>

                {/* Right side: business info + stats + features */}
                <div className="p-6 sm:p-7 flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`p-2.5 rounded-xl ${a.bg} ring-1 ${a.ring}`}>
                      <Icon className={`w-5 h-5 ${a.text}`} />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-bold text-lg leading-tight">{demo.name}</h2>
                      <p className={`text-xs mt-0.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                        {demo.location}
                      </p>
                    </div>
                  </div>

                  <p className={`text-sm leading-relaxed mb-5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {demo.description}
                  </p>

                  {/* Stats grid: at-a-glance numbers from the demo account */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {demo.stats.map((s, idx) => {
                      const SIcon = s.icon;
                      return (
                        <div
                          key={idx}
                          className={`flex items-start gap-2.5 p-3 rounded-xl ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}
                        >
                          <SIcon className={`w-4 h-4 mt-0.5 shrink-0 ${a.text}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight">{s.label}</p>
                            <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{s.sub}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Feature pills */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {demo.features.map((f, idx) => (
                      <span
                        key={idx}
                        className={`text-[11px] px-2 py-1 rounded ${isDark ? "bg-slate-800 text-slate-300 border border-slate-700" : "bg-slate-100 text-slate-600 border border-slate-200"}`}
                      >
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="mt-auto flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => setActive(demo)}
                      className={`flex-1 bg-gradient-to-r ${a.btn} text-white`}
                      data-testid={`demo-card-${demo.id}-watch-btn`}
                    >
                      <Play className="w-4 h-4 mr-1.5 fill-current" /> Watch demo
                    </Button>
                    <Link to={demo.interactive} className="flex-1">
                      <Button
                        variant="outline"
                        className="w-full"
                        data-testid={`demo-card-${demo.id}-interactive-btn`}
                      >
                        Try it live
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          );
        })}

        {/* CTA */}
        <div
          className={`mt-4 p-6 sm:p-7 rounded-2xl text-center border ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
          data-testid="demos-cta"
        >
          <h3 className="font-bold text-lg">Ready to set up your own?</h3>
          <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Free to start. No credit card. Pick your industry and we'll set up the right dashboard for you.
          </p>
          <Link to="/signup">
            <Button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white" data-testid="demos-create-account-btn">
              Create free account
            </Button>
          </Link>
        </div>
      </div>

      {/* Video modal */}
      <Dialog open={!!active} onOpenChange={handleModalChange}>
        <DialogContent
          className="max-w-4xl p-0 overflow-hidden bg-slate-950 border-slate-800 text-slate-100"
          data-testid="demo-video-modal"
        >
          <DialogTitle className="sr-only">{active?.name} demo video</DialogTitle>
          <DialogDescription className="sr-only">
            Narrated walkthrough of the {active?.name} demo dashboard.
          </DialogDescription>

          {/* Modal header strip with title (the Dialog component provides
              its own X close button in the top-right corner). */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900">
            <div className="min-w-0 pr-12">
              <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold truncate">
                {active?.tagline}
              </p>
              <p className="text-sm font-semibold truncate">{active?.name}</p>
            </div>
          </div>

          {active && (
            <video
              key={active.id}
              src={active.video}
              poster={active.preview}
              controls
              autoPlay
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="demo-video-player"
            />
          )}

          {/* Footer: jump to interactive */}
          <div className="flex flex-col sm:flex-row gap-2 px-4 py-3 border-t border-slate-800 bg-slate-900">
            <Button
              onClick={() => setActive(null)}
              variant="ghost"
              className="text-slate-300 hover:text-white"
              data-testid="demo-video-modal-close-btn"
            >
              Close
            </Button>
            {active && (
              <Link to={active.interactive} className="sm:ml-auto" onClick={() => setActive(null)}>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white w-full sm:w-auto" data-testid="demo-video-try-live-btn">
                  Try it live →
                </Button>
              </Link>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
